import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '../../../../components/Button';
import { Flex } from '../../../../components/Flex';
import { Input } from '../../../../components/Input';
import { Text } from '../../../../components/Text';
import { Modal } from '../../../../components/modal/Modal';
import { ChangePasswordDto } from '../../../../model/settings/dto/ChangePasswordDto';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { userSliceSelectors } from '../../../../redux/user/user.selector';
import { userActions } from '../../../../redux/user/user.slice';
import { Spacing } from '../../../../theme/Spacing';
import { AtiraToast } from '../../../../utils/AtiraToast';
import { onSetTypedCRUDErrors } from '../../../../utils/onSetTypedCRUDErrors';

const validatePasswordsMatch = (
  password?: string,
  passwordConfirm?: string,
) => {
  return password === passwordConfirm;
};

const StyledButton = styled(Button)`
  height: 2.3rem;
  margin-top: ${Spacing.m};

  &:hover {
    background-color: ${({ theme }) => theme.mainHover};
  }
`;

type SettingsPasswordChangeModalProps = {
  open: boolean;
  onClose: VoidFunction;
};

export const SettingsPasswordChangeModal: React.FC<
  SettingsPasswordChangeModalProps
> = ({ open = false, onClose }) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    trigger,
    formState: { dirtyFields },
  } = useForm<ChangePasswordDto & { newPasswordConfirm?: string }>({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
      userId,
    },
  });

  const [newPassword, newPasswordConfirm] = watch([
    'newPassword',
    'newPasswordConfirm',
  ]);

  const onLocalClose = () => {
    reset({
      oldPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
      userId,
    });
    clearErrors();
    onClose();
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      delete dto.newPasswordConfirm;

      await dispatch(userActions.changePassword(dto)).unwrap();

      AtiraToast.success(t('settings.general.changePassword.success'));

      onLocalClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dirtyFields.newPassword && dirtyFields.newPasswordConfirm) {
      trigger(['newPassword', 'newPasswordConfirm']);
    }
  }, [newPassword, newPasswordConfirm, trigger, dirtyFields]);

  return (
    <Modal
      title={t('settings.general.changePassword.modal.title')}
      open={open}
      onClose={onLocalClose}
    >
      <Flex width={'100%'} flexDirection="column" gap="m">
        <Text color="fgSecondary" fontSize="s">
          {t('settings.general.changePassword.modal.description')}
        </Text>

        <Controller
          control={control}
          name="oldPassword"
          rules={{
            required: t('common.this_field_required'),
            minLength: {
              value: 6,
              message: t('settings.general.changePassword.modal.error.range'),
            },
            maxLength: {
              value: 64,
              message: t('settings.general.changePassword.modal.error.range'),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t(
                'settings.general.changePassword.modal.oldPassword.title',
              )}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              type="password"
              required
            />
          )}
        />

        <Controller
          control={control}
          name="newPassword"
          rules={{
            required: t('common.this_field_required'),
            minLength: {
              value: 6,
              message: t('settings.general.changePassword.modal.error.range'),
            },
            maxLength: {
              value: 64,
              message: t('settings.general.changePassword.modal.error.range'),
            },
            validate: (value) =>
              validatePasswordsMatch(value, newPasswordConfirm) ||
              t('settings.general.changePassword.modal.error.match'),
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t(
                'settings.general.changePassword.modal.newPassword.title',
              )}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              type="password"
              required
            />
          )}
        />

        <Controller
          control={control}
          name="newPasswordConfirm"
          rules={{
            required: t('common.this_field_required'),
            validate: (value) =>
              validatePasswordsMatch(value, newPassword) ||
              t('settings.general.changePassword.modal.error.match'),
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t(
                'settings.general.changePassword.modal.confirmPassword.title',
              )}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              type="password"
              required
            />
          )}
        />

        <StyledButton loading={loading} onClick={handleSubmit(onSubmit)}>
          {t('settings.general.changePassword.modal.button')}
        </StyledButton>
      </Flex>
    </Modal>
  );
};
